import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_URLS, BASE_URLS } from "./apis/urls";
import Api from "./utils/apiCall";

interface AnnouncementState {
  data: any;
  createData: any;
  updateData: any;
  favouriteData: any;
  removeData: any;
  loading: "idle" | "pending" | "fulfilled" | "rejected";
  error: string | null;
  action:
    | "save_Schedular"
    | "list"
    | "create"
    | "update"
    | "favourite"
    | "remove"
    | null;
}

const announcementInitialState: AnnouncementState = {
  data: null,
  createData: null,
  updateData: null,
  favouriteData: null,
  removeData: null,
  loading: "idle",
  error: null,
  action: null,
};

export const getAllAnnouncements: any = createAsyncThunk(
  "announcement/getAllAnnouncements",
  async (payload: any, thunkAPI) => {
    try {
      const requestBody = {
        fromDate: payload.fromDate || "",
        toDate: payload.toDate || "",
        // isActive: payload.isActive !== undefined ? payload.isActive : true,
        category: payload.category || "",
        department: payload.department || "",
        designation: payload.designation || "",
        type: payload.type || "",
        title: payload.title || "",
        created_by: payload.created_by || "",
        updated_by: payload.updated_by || "",
        fav: payload.fav !== undefined ? payload.fav : undefined,
        page: payload.page || 1,
        limit: payload.limit || 10,
      };

      const response = await Api.post(
        API_URLS.ANNOUNCEMENTS.GET_ALL,
        requestBody
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const upsertAnnouncement: any = createAsyncThunk(
  "announcement/upsertAnnouncement",
  async (payload: any, thunkAPI) => {
    try {
      const requestBody: any = {
        title: payload.title,
        category: payload.category || "",
        description: payload.description,
        type: payload.type || "",
        designation: payload.designation || "",
        department: payload.department || "",
        isActive: payload.isActive !== undefined ? payload.isActive : true,
        image: payload.imageBase64 || "",
        pdf: payload.pdfBase64 || "",
        wing: payload.wing || "",
        start_date: payload.start_date || "",
        end_date: payload.end_date || "",
      };

      if (payload.id) {
        requestBody.id = payload.id;
      }

      const response = payload.id
        ? await Api.post(API_URLS.ANNOUNCEMENTS.UPSERT, requestBody)
        : await Api.post(API_URLS.ANNOUNCEMENTS.INSERT, requestBody);

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const saveSchedular: any = createAsyncThunk(
  "schedular/save",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.ANNOUNCEMENTS.SAVE_SCHEDULAR,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const toggleFavourite: any = createAsyncThunk(
  "announcement/toggleFavourite",
  async (payload: { id: number }, thunkAPI) => {
    try {
      const response = await Api.post(
        BASE_URLS.BASE_URL + "announcement/favourite",
        { id: payload.id }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const removeAnnouncement: any = createAsyncThunk(
  "announcement/removeAnnouncement",
  async (payload: { id: number }, thunkAPI) => {
    try {
      const response = await Api.post(
        BASE_URLS.BASE_URL + "announcement/remove",
        { id: payload.id }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// export const toggleFavourite: any = createAsyncThunk(
//   "announcement/toggleFavourite",
//   async (payload: { id: number }, thunkAPI) => {
//     try {
//       const response = await Api.post(
//         API_URLS.ANNOUNCEMENTS.TOGGLE_FAVOURITE ||
//           BASE_URLS.BASE_URL + "announcement/toggle-favourite",
//         payload
//       );
//       return response.data;
//     } catch (error: any) {
//       const errorMessage =
//         error?.response?.data?.message ||
//         error?.response?.data?.data?.message ||
//         "Failed to toggle favourite status.";
//       return thunkAPI.rejectWithValue(errorMessage);
//     }
//   }
// );

const announcementSlice = createSlice({
  name: "announcement",
  initialState: announcementInitialState,
  reducers: {
    resetAll: (state: any) => {
      state.loading = "idle";
      state.error = null;
      state.data = null;
      state.createData = null;
      state.updateData = null;
      state.favouriteData = null;
      state.removeData = null;
      state.action = null;
    },
    clearCreateData: (state: any) => {
      state.createData = null;
    },
    clearUpdateData: (state: any) => {
      state.updateData = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllAnnouncements.pending, (state) => {
      state.loading = "pending";
      state.data = null;
      state.error = null;
      state.action = "list";
    });
    builder.addCase(getAllAnnouncements.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.data = action.payload;
      state.error = null;
      state.action = "list";
    });
    builder.addCase(getAllAnnouncements.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.data = null;
      state.action = "list";
    });

    builder.addCase(upsertAnnouncement.pending, (state) => {
      state.loading = "pending";
      state.error = null;
      state.action = "create";
    });
    builder.addCase(upsertAnnouncement.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.createData = action.payload;
      state.updateData = action.payload;
      state.error = null;
      state.action = "create";
    });
    builder.addCase(upsertAnnouncement.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.action = "create";
    });

    builder.addCase(saveSchedular.pending, (state) => {
      state.loading = "pending";
      state.error = null;
      state.action = "save_Schedular";
    });
    builder.addCase(saveSchedular.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.error = null;
      state.action = "save_Schedular";
    });
    builder.addCase(saveSchedular.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.action = "save_Schedular";
    });

    builder.addCase(toggleFavourite.pending, (state) => {
      state.loading = "pending";
      state.error = null;
      state.action = "favourite";
    });
    builder.addCase(toggleFavourite.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.favouriteData = action.payload;
      state.error = null;
      state.action = "favourite";
    });
    builder.addCase(toggleFavourite.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.action = "favourite";
    });

    builder.addCase(removeAnnouncement.pending, (state) => {
      state.loading = "pending";
      state.error = null;
      state.action = "remove";
    });
    builder.addCase(removeAnnouncement.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.removeData = action.payload;
      state.error = null;
      state.action = "remove";
    });
    builder.addCase(removeAnnouncement.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.action = "remove";
    });
  },
});

export const { resetAll, clearCreateData, clearUpdateData } =
  announcementSlice.actions;
export default announcementSlice.reducer;
